import { runInAction } from 'mobx';
import {
  Contract,
  ContractData,
  ContractType,
  UserApproval,
  UserCoupon,
  UserCouponStatus,
} from '@monorepo/types';
import { useStore } from '@hub/helpers/use-store';
import { QueryConfigKeys } from '@hub/utils/react-query/query-config';
import {
  approveUser,
  createUserCoupon,
  deleteProfilePicture,
  sendRiseInvitation,
  signUserContract,
  uploadProfilePicture,
} from '@hub/utils/react-query/query-functions';
import { QUERY_KEYS } from '@hub/utils/react-query/query-keys';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const CONFIG_KEY = QueryConfigKeys.USERS;

// export const useUpdateUser = () => {
//   const {
//     dataStore: { userStore },
//   } = useStore();

//   return useMutation<User, Error, Partial<User>>({
//     mutationFn: updateUser,
//     onSuccess: (user) => {
//       userStore.setCurrentUser({ ...userStore.currentUser, ...user });
//     },
//   });
// };

export const useSignUserContract = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Contract,
    Error,
    { contractType: ContractType; contractData?: ContractData }
  >({
    mutationFn: signUserContract,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS[CONFIG_KEY].CONTRACTS,
      });
    },
  });
};

export const useCreateUserCoupon = () => {
  const {
    dataStore: { couponStore },
  } = useStore();

  const queryClient = useQueryClient();

  return useMutation<
    { coupon: UserCoupon | null; status: UserCouponStatus },
    Error,
    { org: string; name: string }
  >({
    mutationFn: createUserCoupon,
    onSuccess: (data) => {
      const { coupon, status } = data;

      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USERS.COUPONS });

      runInAction(() => {
        if (coupon && status === UserCouponStatus.Valid) {
          couponStore.coupons = [...couponStore.coupons, coupon];
        }
      });
    },
  });
};

export const useUploadProfilePicture = () => {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, FormData>({
    mutationFn: uploadProfilePicture,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS[CONFIG_KEY].UPLOAD_PROFILE_PICTURE,
      });
    },
  });
};

export const useDeleteProfilePicture = () => {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, void>({
    mutationFn: deleteProfilePicture,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS[CONFIG_KEY].DELETE_PROFILE_PICTURE,
      });
    },
  });
};

export const useSendRiseInvitation = () => {
  return useMutation<unknown, Error, void>({
    mutationFn: sendRiseInvitation,
  });
};

export const useApproveUser = () => {
  return useMutation<unknown, Error, UserApproval>({
    mutationFn: approveUser,
  });
};
